import NoImageFound from "@/components/NoImageFound";
import { getAuctionItemLink } from "@/lib/links";
import classNames from "classnames";
import moment from "moment";
import Link from "next/link";
import React, { useEffect, useState } from "react";
import "moment/locale/sv";
import { formatPrice } from "@/lib/utils";

interface BidNotificationProps {
  notification: any;
  isNew?: boolean;
}

const BidNotification: React.FC<BidNotificationProps> = ({
  notification,
  isNew = false,
}) => {
  const { data, meta, created, action } = notification;
  const [showAnimation, setShowAnimation] = useState(isNew);
  const [showPulse, setShowPulse] = useState(false);

  const auctionItem = data?.auctionItem;

  useEffect(() => {
    if (isNew) {
      setShowAnimation(true);

      const pulseTimer = setTimeout(() => {
        setShowPulse(true);
      }, 600);

      const animationTimer = setTimeout(() => {
        setShowAnimation(false);
      }, 2000);

      const pulseEndTimer = setTimeout(() => {
        setShowPulse(false);
      }, 5000);

      return () => {
        clearTimeout(pulseTimer);
        clearTimeout(animationTimer);
        clearTimeout(pulseEndTimer);
      };
    }
  }, [isNew]);

  const images =
    auctionItem?.images && Object.values(auctionItem?.images as any);
  const image = images?.length ? images[0] : ("" as any);
  const url = auctionItem ? getAuctionItemLink(auctionItem) : false;

  const actionTextDescriptions = {
    overbid_auto: "Du har blivit överbjuden genom autobud på",
    overbid_normal: "Du har blivit överbjuden på",
  } as any;

  const notificationClass = classNames(
    "bg-white w-full py-4 px-4 relative z-10 transition-all duration-500 ease-out",
    {
      "animate-bounce-in border-l-4 border-yellow shadow-lg": showAnimation,
      "animate-pulse-glow border-l-4 border-yellow":
        showPulse && !showAnimation,
      "hover:shadow-md": !showAnimation && !showPulse,
    }
  );

  return (
    <div className={notificationClass}>
      <div className="flex items-center">
        <div className="flex-shrink-0 pt-0.5">
          {image ? (
            <img className="h-10 w-10 rounded-full" src={image} alt="" />
          ) : (
            <div className="w-[50px] h-[50px]">
              <NoImageFound text="" containerClass="!min-h-full" useIcon />
            </div>
          )}
        </div>

        <div className="ml-3 flex-1">
          {auctionItem ? (
            <p className="text-md font-medium text-gray-900">
              {actionTextDescriptions[notification?.action] ||
                "Du har blivit överbjuden på"}{" "}
              <Link
                href={url || ""}
                className="!no-underline text-blue"
                target="_blank"
              >
                <strong>{auctionItem.itemTitle}</strong>
              </Link>{" "}
              {meta?.leadingBid && (
                <>
                  nytt ledande bud är{" "}
                  <strong>{formatPrice(meta?.leadingBid)}</strong>
                </>
              )}
              {created && (
                <div className="text-sm text-gray-500">
                  {moment(created).fromNow()}
                </div>
              )}
            </p>
          ) : (
            <p className="text-md font-medium text-gray-900">
              Auktionen du har blivit överbjuden på har tagits bort.
            </p>
          )}
          <span></span>
        </div>
      </div>
    </div>
  );
};

export default BidNotification;
